import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MessageSquare, Send, Paperclip, Plus, X, AlertTriangle,
  ArrowRight, Loader2, FileText, Image as ImageIcon, Code, Bot, User,
  Globe, Search, ExternalLink, Trash2, Pencil, MoreVertical, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_MESSAGES = 50;

const FILE_ICONS = {
  image: ImageIcon,
  text: FileText,
  code: Code,
  document: FileText,
};

function getFileCategory(filename) {
  const ext = filename.split('.').pop().toLowerCase();
  if (['png','jpg','jpeg','webp','gif'].includes(ext)) return 'image';
  if (['py','js','jsx','ts','tsx','html','css','sql'].includes(ext)) return 'code';
  if (['pdf','docx','xlsx'].includes(ext)) return 'document';
  return 'text';
}

const WebmasterChat = ({ monitorId, companyName, companyUrl }) => {
  const [dialogs, setDialogs] = useState([]);
  const [activeDialogId, setActiveDialogId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingDialogs, setLoadingDialogs] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dialogStatus, setDialogStatus] = useState('active');
  const [creatingNewDialog, setCreatingNewDialog] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [parsingSite, setParsingSite] = useState(false);
  const [siteData, setSiteData] = useState(null);
  const [editingDialogId, setEditingDialogId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [contextMenuId, setContextMenuId] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenuId) return;
    const handler = () => setContextMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [contextMenuId]);

  // Load dialogs
  useEffect(() => {
    loadDialogs();
  }, [monitorId]);

  const loadDialogs = async () => {
    setLoadingDialogs(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/chat/dialogs?monitor_id=${monitorId}`, { withCredentials: true });
      setDialogs(data);
      // Auto-select the latest active dialog or create one
      const active = data.filter(d => d.status === 'active');
      if (active.length > 0) {
        setActiveDialogId(active[active.length - 1].id);
      } else if (data.length === 0) {
        await createNewDialog();
      }
    } catch (err) {
      console.error('Error loading dialogs:', err);
    } finally {
      setLoadingDialogs(false);
    }
  };

  // Load messages when dialog changes
  useEffect(() => {
    if (activeDialogId) {
      loadMessages(activeDialogId);
    }
  }, [activeDialogId]);

  const loadMessages = async (dialogId) => {
    setLoadingMessages(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/chat/dialogs/${dialogId}/messages`,
        { withCredentials: true }
      );
      setMessages(data);
      const dialog = dialogs.find(d => d.id === dialogId);
      setDialogStatus(dialog?.status || 'active');
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setLoadingMessages(false);
    }
  };

  const createNewDialog = async () => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chat/dialogs`,
        { monitor_id: monitorId },
        { withCredentials: true }
      );
      setDialogs(prev => [...prev, data]);
      setActiveDialogId(data.id);
      setMessages([]);
      setDialogStatus('active');
      return data;
    } catch (err) {
      console.error('Error creating dialog:', err);
    }
  };

  const createNewDialogFromSummary = async () => {
    if (!activeDialogId) return;
    setCreatingNewDialog(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/chat/dialogs/${activeDialogId}/new-from-summary`,
        {},
        { withCredentials: true }
      );
      // Update old dialog status in local state
      setDialogs(prev => prev.map(d =>
        d.id === activeDialogId ? { ...d, status: 'archived' } : d
      ));
      setDialogs(prev => [...prev, data]);
      setActiveDialogId(data.id);
      setMessages([]);
      setDialogStatus('active');
    } catch (err) {
      console.error('Error creating new dialog from summary:', err);
    } finally {
      setCreatingNewDialog(false);
    }
  };

  const handleDeleteDialog = async (dialogId) => {
    if (!window.confirm('Delete this dialog and all messages?')) return;
    try {
      await axios.delete(`${API_URL}/api/chat/dialogs/${dialogId}`, { withCredentials: true });
      setDialogs(prev => prev.filter(d => d.id !== dialogId));
      if (activeDialogId === dialogId) {
        const remaining = dialogs.filter(d => d.id !== dialogId);
        if (remaining.length > 0) {
          setActiveDialogId(remaining[remaining.length - 1].id);
        } else {
          setActiveDialogId(null);
          setMessages([]);
          await createNewDialog();
        }
      }
    } catch (err) {
      console.error('Error deleting dialog:', err);
    }
    setContextMenuId(null);
  };

  const handleRenameDialog = async (dialogId) => {
    const trimmed = editTitle.trim();
    if (!trimmed) { setEditingDialogId(null); return; }
    try {
      await axios.put(
        `${API_URL}/api/chat/dialogs/${dialogId}/rename`,
        { title: trimmed },
        { withCredentials: true }
      );
      setDialogs(prev => prev.map(d => d.id === dialogId ? { ...d, title: trimmed } : d));
    } catch (err) {
      console.error('Error renaming dialog:', err);
    }
    setEditingDialogId(null);
  };

  const startRenaming = (dialog) => {
    setEditingDialogId(dialog.id);
    setEditTitle(dialog.title);
    setContextMenuId(null);
  };

  const handleParseSite = async () => {
    if (!activeDialogId || parsingSite || sending) return;
    setParsingSite(true);
    try {
      // First, parse the project URL
      const { data: parseResult } = await axios.post(
        `${API_URL}/api/chat/parse-project`,
        { monitor_id: monitorId },
        { withCredentials: true }
      );
      setSiteData(parseResult);

      if (!parseResult.success) {
        setParsingSite(false);
        return;
      }

      // Send the parsed content as a message to the chat
      const parseMessage = `Analyze my website ${companyUrl || ''} based on parsing. Provide SEO/AEO optimization recommendations.\n\n${parseResult.formatted_content}`;

      setSending(true);
      const tempUserMsg = {
        role: 'user',
        content: `Analyze my website ${companyUrl || ''} — parsing and page analysis started`,
        files: [],
        created_at: new Date().toISOString(),
        is_parse: true,
      };
      setMessages(prev => [...prev, tempUserMsg]);

      const { data: chatResp } = await axios.post(
        `${API_URL}/api/chat/dialogs/${activeDialogId}/messages`,
        {
          dialog_id: activeDialogId,
          content: parseMessage,
        },
        { withCredentials: true }
      );

      setMessages(prev => {
        const withoutTemp = prev.slice(0, -1);
        return [
          ...withoutTemp,
          { role: 'user', content: `Site analysis ${companyUrl || ''}`, files: [], created_at: chatResp.user_message.created_at, is_parse: true },
          { role: 'assistant', content: chatResp.assistant_message.content, files: [], created_at: chatResp.assistant_message.created_at },
        ];
      });

      if (chatResp.dialog_status === 'context_limit') {
        setDialogStatus('context_limit');
      }
    } catch (err) {
      console.error('Error parsing site:', err);
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: '_Error parsing website. Please try again later._', files: [], created_at: new Date().toISOString() },
      ]);
    } finally {
      setParsingSite(false);
      setSending(false);
    }
  };

  const handleSend = async () => {
    if ((!input.trim() && attachedFiles.length === 0) || sending) return;
    const messageText = input.trim();
    setInput('');
    setSending(true);

    // Optimistic add user message
    const tempUserMsg = {
      role: 'user',
      content: messageText,
      files: attachedFiles.map(f => ({ filename: f.filename, file_id: f.file_id })),
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, tempUserMsg]);
    const fileIds = attachedFiles.map(f => f.file_id);
    setAttachedFiles([]);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/chat/dialogs/${activeDialogId}/messages`,
        {
          dialog_id: activeDialogId,
          content: messageText,
          file_ids: fileIds.length > 0 ? fileIds : null,
        },
        { withCredentials: true }
      );

      // Replace optimistic message + add assistant response
      setMessages(prev => {
        const withoutTemp = prev.slice(0, -1);
        return [
          ...withoutTemp,
          { role: 'user', content: data.user_message.content, files: data.user_message.files, created_at: data.user_message.created_at },
          { role: 'assistant', content: data.assistant_message.content, files: [], created_at: data.assistant_message.created_at, is_error: data.assistant_message.is_error },
        ];
      });

      // Update dialog message count in sidebar
      setDialogs(prev => prev.map(d =>
        d.id === activeDialogId ? { ...d, message_count: data.message_count } : d
      ));

      if (data.dialog_status === 'context_limit') {
        setDialogStatus('context_limit');
        setDialogs(prev => prev.map(d =>
          d.id === activeDialogId ? { ...d, status: 'context_limit', message_count: data.message_count } : d
        ));
      }
    } catch (err) {
      console.error('Error sending message:', err);
      const errorDetail = err.response?.data?.detail || 'Error sending message';
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: `_Error: ${errorDetail}_`, files: [], created_at: new Date().toISOString() },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await axios.post(`${API_URL}/api/chat/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setAttachedFiles(prev => [...prev, data]);
      } catch (err) {
        console.error('File upload error:', err);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.file_id !== fileId));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const activeDialog = dialogs.find(d => d.id === activeDialogId);

  return (
    <div className="flex h-[calc(100vh-280px)] min-h-[500px] bg-white border border-[#E2DDD8] overflow-hidden" data-testid="webmaster-chat">
      {/* Left sidebar - Dialogs */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-0'} border-r border-[#E2DDD8] flex-shrink-0 transition-all duration-200 overflow-hidden`}>
        <div className="w-64 h-full flex flex-col">
          <div className="p-3 border-b border-[#E2DDD8] flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Dialogs</span>
            <button
              onClick={createNewDialog}
              className="p-1.5 hover:bg-slate-100 rounded transition-colors"
              data-testid="new-dialog-button"
              title="New Dialog"
            >
              <Plus className="w-4 h-4 text-[#1BB594]" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loadingDialogs ? (
              <div className="p-4 text-center text-slate-400 text-sm">Loading...</div>
            ) : (
              dialogs.map(dialog => (
                <div
                  key={dialog.id}
                  className={`relative group w-full border-b border-[#E2DDD8]/50 transition-colors ${
                    activeDialogId === dialog.id
                      ? 'bg-[#1BB594]/5 border-l-2 border-l-[#1BB594]'
                      : 'hover:bg-slate-50'
                  }`}
                  data-testid={`dialog-item-${dialog.id}`}
                >
                  {editingDialogId === dialog.id ? (
                    <div className="flex items-center gap-1 px-3 py-2.5">
                      <input
                        autoFocus
                        value={editTitle}
                        onChange={e => setEditTitle(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleRenameDialog(dialog.id);
                          if (e.key === 'Escape') setEditingDialogId(null);
                        }}
                        onBlur={() => handleRenameDialog(dialog.id)}
                        className="flex-1 text-sm border border-[#1BB594] rounded px-2 py-1 focus:outline-none"
                        data-testid={`rename-input-${dialog.id}`}
                      />
                      <button
                        onClick={() => handleRenameDialog(dialog.id)}
                        className="p-1 text-[#1BB594] hover:bg-[#1BB594]/10 rounded"
                        data-testid={`rename-confirm-${dialog.id}`}
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setActiveDialogId(dialog.id); setContextMenuId(null); }}
                      className="w-full text-left px-4 py-3 flex items-center gap-2"
                    >
                      <MessageSquare className={`w-4 h-4 flex-shrink-0 ${
                        dialog.status === 'archived' ? 'text-slate-300' :
                        dialog.status === 'context_limit' ? 'text-amber-500' :
                        'text-[#1BB594]'
                      }`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium truncate ${
                          dialog.status === 'archived' ? 'text-slate-400' : 'text-slate-800'
                        }`}>{dialog.title}</p>
                        <p className="text-[10px] text-slate-400">
                          {dialog.status === 'archived' && 'Archive'}
                          {dialog.status === 'context_limit' && 'Limit'}
                          {dialog.status === 'active' && `${dialog.message_count || 0} msg`}
                        </p>
                      </div>
                    </button>
                  )}
                  {/* Context menu trigger */}
                  {editingDialogId !== dialog.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setContextMenuId(contextMenuId === dialog.id ? null : dialog.id); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-slate-200 transition-opacity"
                      data-testid={`dialog-menu-${dialog.id}`}
                    >
                      <MoreVertical className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  )}
                  {/* Dropdown menu */}
                  {contextMenuId === dialog.id && (
                    <div className="absolute right-2 top-full z-20 bg-white border border-[#E2DDD8] shadow-lg rounded py-1 w-36" data-testid={`dialog-dropdown-${dialog.id}`}>
                      <button
                        onClick={(e) => { e.stopPropagation(); startRenaming(dialog); }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
                        data-testid={`dialog-rename-btn-${dialog.id}`}
                      >
                        <Pencil className="w-3 h-3" /> Rename
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteDialog(dialog.id); }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50"
                        data-testid={`dialog-delete-btn-${dialog.id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Delete
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat header */}
        <div className="px-4 py-3 border-b border-[#E2DDD8] flex items-center gap-3 bg-slate-50/50">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 hover:bg-slate-200 rounded transition-colors lg:hidden"
            data-testid="toggle-sidebar-button"
          >
            <MessageSquare className="w-4 h-4 text-slate-600" />
          </button>
          <Bot className="w-5 h-5 text-[#1BB594]" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-800 truncate">
              GEO/AEO Assistant
              {companyName && <span className="font-normal text-slate-500"> — {companyName}</span>}
            </p>
            {activeDialog && (
              <p className="text-[11px] text-slate-400">{activeDialog.title} &middot; {activeDialog.message_count || 0}/{MAX_MESSAGES} msg</p>
            )}
          </div>
          {dialogStatus === 'context_limit' && (
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-1">
              Limit
            </span>
          )}
          {dialogStatus !== 'context_limit' && (
            <button
              onClick={handleParseSite}
              disabled={parsingSite || sending || !activeDialogId}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border border-[#1BB594]/30 text-[#1BB594] hover:bg-[#1BB594] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="parse-site-button"
              title="Parse and analyze project website"
            >
              {parsingSite ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Parsing...</>
              ) : (
                <><Search className="w-3.5 h-3.5" /> Site Analysis</>
              )}
            </button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chat-messages-area">
          {loadingMessages ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-6 h-6 text-[#1BB594] animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Bot className="w-12 h-12 text-slate-200 mb-4" />
              <p className="text-lg font-bold text-slate-800 mb-2">GEO/AEO Assistant</p>
              <p className="text-sm text-slate-500 max-w-md mb-6">
                Ask a question about SEO/AEO optimization of your website for AI search engines.
                I can help with technical audits, content strategy, and visibility monitoring.
              </p>
              <button
                onClick={handleParseSite}
                disabled={parsingSite || !activeDialogId}
                className="flex items-center gap-2 px-6 py-3 bg-[#1BB594] text-white font-bold text-xs uppercase tracking-widest hover:bg-[#159A7F] transition-colors disabled:opacity-50"
                data-testid="parse-site-empty-button"
              >
                {parsingSite ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing website...</>
                ) : (
                  <><Search className="w-4 h-4" /> Analyze Website</>
                )}
              </button>
              {companyUrl && (
                <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> {companyUrl}
                </p>
              )}
              <div className="mt-6 flex flex-wrap gap-2 justify-center max-w-lg">
                {[
                  'How to improve visibility in ChatGPT?',
                  'Check my robots.txt',
                  'Do I need JSON-LD markup?',
                  'Strategy for AI search engines',
                ].map((q, i) => (
                  <button
                    key={i}
                    onClick={() => { setInput(q); }}
                    className="px-3 py-1.5 text-xs text-slate-600 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-full transition-colors"
                    data-testid={`quick-question-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`chat-message-${idx}`}
              >
                {msg.role === 'assistant' && (
                  <div className={`w-7 h-7 rounded flex items-center justify-center flex-shrink-0 mt-1 ${
                    msg.is_error ? 'bg-amber-500' : 'bg-[#1BB594]'
                  }`}>
                    {msg.is_error ? <AlertTriangle className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
                  </div>
                )}
                <div className={`max-w-[75%] ${
                  msg.role === 'user'
                    ? 'bg-[#1BB594] text-white rounded-2xl rounded-br-sm px-4 py-3'
                    : msg.is_error
                      ? 'bg-amber-50 text-amber-900 border border-amber-200 rounded-2xl rounded-bl-sm px-4 py-3'
                      : 'bg-slate-100 text-slate-800 rounded-2xl rounded-bl-sm px-4 py-3'
                }`}>
                  {msg.is_parse && (
                    <div className="flex items-center gap-1.5 mb-2 text-[10px] font-bold uppercase tracking-wider opacity-70">
                      <Globe className="w-3 h-3" /> Parse website
                    </div>
                  )}
                  {msg.files && msg.files.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {msg.files.map((f, fi) => {
                        const cat = getFileCategory(f.filename);
                        const Icon = FILE_ICONS[cat] || FileText;
                        return (
                          <span key={fi} className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded ${
                            msg.role === 'user' ? 'bg-white/20 text-white/90' : 'bg-white text-slate-600 border border-slate-200'
                          }`}>
                            <Icon className="w-3 h-3" />{f.filename}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  <div className={`text-sm leading-relaxed ${msg.role === 'assistant' ? 'prose prose-sm prose-slate max-w-none' : ''}`}>
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({children}) => <h1 style={{fontSize:'1.3em',fontWeight:700,margin:'16px 0 8px',borderBottom:'1px solid #e2e8f0',paddingBottom:6}}>{children}</h1>,
                          h2: ({children}) => <h2 style={{fontSize:'1.15em',fontWeight:700,margin:'14px 0 6px',color:'#1e293b'}}>{children}</h2>,
                          h3: ({children}) => <h3 style={{fontSize:'1.05em',fontWeight:600,margin:'12px 0 4px',color:'#334155'}}>{children}</h3>,
                          h4: ({children}) => <h4 style={{fontSize:'1em',fontWeight:600,margin:'10px 0 4px',color:'#475569'}}>{children}</h4>,
                          table: ({children}) => <div style={{overflowX:'auto',margin:'12px 0'}}><table style={{borderCollapse:'collapse',width:'100%',fontSize:'0.85em'}}>{children}</table></div>,
                          thead: ({children}) => <thead style={{backgroundColor:'#f1f5f9'}}>{children}</thead>,
                          th: ({children}) => <th style={{border:'1px solid #e2e8f0',padding:'6px 10px',fontWeight:600,textAlign:'left'}}>{children}</th>,
                          td: ({children}) => <td style={{border:'1px solid #e2e8f0',padding:'6px 10px'}}>{children}</td>,
                          ul: ({children}) => <ul style={{paddingLeft:20,margin:'8px 0',listStyleType:'disc'}}>{children}</ul>,
                          ol: ({children}) => <ol style={{paddingLeft:20,margin:'8px 0',listStyleType:'decimal'}}>{children}</ol>,
                          li: ({children}) => <li style={{margin:'3px 0'}}>{children}</li>,
                          blockquote: ({children}) => <blockquote style={{borderLeft:'3px solid #1BB594',paddingLeft:12,margin:'10px 0',color:'#64748b',fontStyle:'italic'}}>{children}</blockquote>,
                          code: ({inline, children}) => inline
                            ? <code style={{backgroundColor:'#f1f5f9',padding:'1px 4px',borderRadius:3,fontSize:'0.9em',fontFamily:'monospace'}}>{children}</code>
                            : <pre style={{backgroundColor:'#1e293b',color:'#e2e8f0',padding:12,borderRadius:6,overflowX:'auto',margin:'10px 0',fontSize:'0.85em'}}><code>{children}</code></pre>,
                          p: ({children}) => <p style={{margin:'6px 0',lineHeight:1.6}}>{children}</p>,
                          hr: () => <hr style={{border:'none',borderTop:'1px solid #e2e8f0',margin:'12px 0'}} />,
                          a: ({href, children}) => <a href={href} target="_blank" rel="noopener noreferrer" style={{color:'#1BB594',textDecoration:'underline'}}>{children}</a>,
                          strong: ({children}) => <strong style={{fontWeight:700}}>{children}</strong>,
                        }}
                      >{msg.content}</ReactMarkdown>
                    ) : (
                      <span className="whitespace-pre-wrap">{msg.content}</span>
                    )}
                  </div>
                </div>
                {msg.role === 'user' && (
                  <div className="w-7 h-7 rounded bg-slate-200 flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="w-4 h-4 text-slate-600" />
                  </div>
                )}
              </div>
            ))
          )}
          {sending && (
            <div className="flex gap-3 justify-start" data-testid="chat-thinking-indicator">
              <div className="w-7 h-7 rounded bg-[#1BB594] flex items-center justify-center flex-shrink-0">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-slate-100 rounded-2xl rounded-bl-sm px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Thinking...
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Context limit banner */}
        {dialogStatus === 'context_limit' && (
          <div className="px-4 py-3 bg-amber-50 border-t border-amber-200 flex items-center justify-between gap-3" data-testid="context-limit-banner">
            <div className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-sm text-amber-800">Context limit reached. Please start a new dialog to continue.</p>
            </div>
            <button
              onClick={createNewDialogFromSummary}
              disabled={creatingNewDialog}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#1BB594] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#159A7F] transition-colors flex-shrink-0 disabled:opacity-50"
              data-testid="new-dialog-from-summary-button"
            >
              {creatingNewDialog ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Creating...</>
              ) : (
                <><ArrowRight className="w-3.5 h-3.5" /> New Dialog</>
              )}
            </button>
          </div>
        )}

        {/* Input area */}
        {dialogStatus !== 'context_limit' && (
          <div className="px-4 py-3 border-t border-[#E2DDD8] bg-white" data-testid="chat-input-area">
            {/* Attached files preview */}
            {attachedFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {attachedFiles.map(f => {
                  const cat = getFileCategory(f.filename);
                  const Icon = FILE_ICONS[cat] || FileText;
                  return (
                    <div key={f.file_id} className="flex items-center gap-1.5 bg-slate-100 border border-slate-200 rounded px-2 py-1 text-xs text-slate-700">
                      <Icon className="w-3.5 h-3.5 text-slate-500" />
                      <span className="max-w-[120px] truncate">{f.filename}</span>
                      <button onClick={() => removeFile(f.file_id)} className="text-slate-400 hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileUpload}
                accept=".png,.jpg,.jpeg,.webp,.gif,.txt,.csv,.md,.json,.xml,.py,.js,.jsx,.ts,.tsx,.html,.css,.sql,.pdf,.docx,.xlsx"
                data-testid="file-upload-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading || sending}
                className="p-2.5 text-slate-400 hover:text-[#1BB594] hover:bg-slate-100 rounded transition-colors disabled:opacity-50"
                data-testid="attach-file-button"
                title="Attach file"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question about SEO/AEO..."
                className="flex-1 resize-none border border-[#E2DDD8] rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#1BB594] focus:ring-1 focus:ring-[#1BB594]/20 min-h-[42px] max-h-[120px]"
                rows={1}
                disabled={sending}
                data-testid="chat-input"
              />
              <button
                onClick={handleSend}
                disabled={(!input.trim() && attachedFiles.length === 0) || sending}
                className="p-2.5 bg-[#1BB594] text-white rounded-lg hover:bg-[#159A7F] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                data-testid="send-message-button"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WebmasterChat;
